import React, { useEffect } from 'react';
import { useGamingStore } from '../../store/gamingStore';
import { ArrowLeft, Download, Loader2, Shield, Clock, HardDrive, Play } from 'lucide-react';

const ReviewLibrary: React.FC = () => {
  const {
    reviewLibrary, isLoadingReviewLibrary, downloadingGameId,
    fetchReviewLibrary, downloadReviewBuild, goBackToHub, openGameDetail,
    downloadedGameSlugs, openDownloadedGame,
  } = useGamingStore();

  useEffect(() => {
    fetchReviewLibrary();
    const interval = window.setInterval(() => {
      fetchReviewLibrary();
    }, 10000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const formatSize = (bytes: number | null) => {
    if (!bytes) return '—';
    if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
    if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const statusLabel: Record<string, { text: string; cls: string }> = {
    in_review: { text: 'En revue', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    waiting_for_review: { text: 'En attente', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    approved: { text: 'Approuvé', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    rejected: { text: 'Rejeté', cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
  };

  const platformLabel: Record<string, string> = {
    windows: 'Windows',
    macos_intel: 'macOS Intel',
    macos_silicon: 'macOS Silicon',
    linux: 'Linux',
  };
  const isElectron = !!window.api;

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar">
      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={goBackToHub}
            className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Hub
          </button>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-gaming-accent" />
            <h1 className="text-2xl font-black text-text-primary">En revue</h1>
          </div>
        </div>

        {isLoadingReviewLibrary ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gaming-accent" />
          </div>
        ) : reviewLibrary.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="h-12 w-12 text-text-muted mx-auto mb-3 opacity-40" />
            <p className="text-text-secondary text-sm">Aucun jeu en revue dans votre bibliothèque.</p>
            <p className="text-text-muted text-xs mt-1">Prenez en charge des soumissions depuis le portail développeur pour les voir ici.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviewLibrary.map(item => {
              const status = statusLabel[item.review_status] || { text: item.review_status, cls: 'bg-bg-tertiary text-text-secondary border-border-color' };
              const isDownloading = downloadingGameId === String(item.store_version_id);
              const itemSlug = item.game_slug || `review-${item.store_version_id}`;
              const isDownloaded = !!downloadedGameSlugs[itemSlug];

              return (
                <div
                  key={item.store_version_id}
                  onClick={() => item.game_slug && openGameDetail(item.game_slug, { fromReview: item.store_version_id, gameId: item.game_id })}
                  className="flex items-center gap-4 bg-bg-secondary border border-border-color rounded-xl p-4 hover:border-gaming-accent/30 transition-colors cursor-pointer"
                >
                  {/* Cover */}
                  <div className="w-14 h-20 rounded-lg overflow-hidden bg-bg-tertiary flex-shrink-0">
                    {item.cover_image_url ? (
                      <img src={item.cover_image_url} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-muted text-lg font-bold">
                        {item.title?.charAt(0)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-text-primary truncate">{item.title}</h3>
                    {item.short_description && (
                      <p className="text-xs text-text-secondary truncate mt-0.5">{item.short_description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${status.cls}`}>
                        {status.text}
                      </span>
                      <span className="text-[10px] text-text-muted">{platformLabel[item.platform] || item.platform}</span>
                      {item.build_version && (
                        <span className="text-[10px] text-text-muted">v{item.build_version} #{item.build_number}</span>
                      )}
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {item.file_size && (
                      <div className="flex items-center gap-1 text-xs text-text-muted">
                        <HardDrive className="h-3 w-3" />
                        {formatSize(item.file_size)}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-xs text-text-muted">
                      <Clock className="h-3 w-3" />
                      {new Date(item.added_at).toLocaleDateString('fr-CA')}
                    </div>

                    {/* Launch or Download */}
                    {!isElectron ? (
                      <button
                        type="button"
                        disabled
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-bg-tertiary border border-border-color text-text-muted text-xs font-semibold cursor-not-allowed"
                        title="Installation is available in Erozion Desktop."
                      >
                        <Download className="h-3.5 w-3.5" />
                        Desktop requis
                      </button>
                    ) : isDownloaded ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); openDownloadedGame(itemSlug, { reviewStoreVersionId: item.store_version_id }); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs font-semibold hover:bg-white/20 transition-colors"
                      >
                        <Play className="h-3.5 w-3.5 fill-white" />
                        Lancer
                      </button>
                    ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadReviewBuild(item.store_version_id); }}
                      disabled={isDownloading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gaming-accent/10 border border-gaming-accent/30 text-gaming-accent text-xs font-semibold hover:bg-gaming-accent/20 transition-colors disabled:opacity-50"
                    >
                      {isDownloading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                      Télécharger
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewLibrary;
